import { useCallback, useState, useEffect, useMemo } from 'react';

import { Box } from '@chakra-ui/react';
import { useToast } from 'components/hooks/useToast';
import { useLocation } from 'react-router-dom';
import { useSelectedView } from 'reducers/viewsSlice';
import {
  getEngagementSurveyReportsApi,
  useGetEngagementSurveyReportsColumns,
} from 'screens/Engagement/LandingPageV2/hooks/dataHooks';
import { CustomHeaderCell } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CustomHeaderCell';
import {
  CellRenderer,
  getColumnDefinitionByType,
} from 'screens/performanceReview/manageAndInsights/TalentPlanning/columnDefinitions';

import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';

import { SurveyGridModal } from './components/SurveyGridHoverModal';
import { useServerSideDatasource } from './hooks/useServerSideDatasource';
import {
  useSelectEngagementSelectedDateRange,
  useSelectEngagementSelectedSplitBy,
  useSelectEngagementSelectedViewBy,
  useSelectEngagementSurveys,
} from './slice/EngagementIntentSlice';

export const NONE_ID = 'NONE';

export const EMPTY_VALUE_ENGAGEMENT_SURVEY_GRID = 'N.E.D';

export const getSurveyDropdownValues = ({ engagementSurveyId, engagementSurveys }) => {
  if (!engagementSurveyId) return [];

  if (Number(engagementSurveyId?.uuid) === -1) {
    return engagementSurveys?.map((survey) => Number(survey.uuid)).filter((surveyId) => surveyId !== -1) ?? [];
  }
  return engagementSurveyId?.uuid ? [Number(engagementSurveyId?.uuid)] : [];
};

/// function to fetch the rows from the server
export const fetchSurveyReportsRows = async ({
  cohortDetails,
  engagementSurveyId,
  engagementSurveys,
  selectedViewById,
  selectedViewByDisplayName,
  selectedSplitById,
  selectedSplitByDisplayName,
  timelineRange,
  toast,
  limit = 1000,
  filterModel = {},
}) => {
  try {
    const surveyReportData = await getEngagementSurveyReportsApi({
      cohortId: cohortDetails?.id,
      feedbackFormIds: getSurveyDropdownValues({ engagementSurveyId, engagementSurveys }),
      // view by dropdown == group by
      rowGroupCols: [
        {
          id: selectedViewById,
          displayName: selectedViewByDisplayName,
          field: selectedViewById,
        },
      ],
      // split by dropdown == pivot
      pivotCols:
        selectedSplitById === NONE_ID
          ? []
          : [
              {
                id: selectedSplitById,
                displayName: selectedSplitByDisplayName,
                field: selectedSplitById,
              },
            ],
      pivotMode: true,
      dateRange: { startTs: timelineRange.startDate, endTs: timelineRange.endDate },
      limit,
      filterModel,
    });

    return surveyReportData;
  } catch (error) {
    console.error('Error fetching survey reports rows:', error);
    toast({
      title: error?.message || 'An unknown error has occurred, please try again',
      status: 'error',
    });

    throw new Error('Error fetching survey reports rows');
  }
};

const customGroupCellRenderer = (params) => {
  const {
    colDef: { cellRendererParams },
  } = params || { colDef: {} };
  const { columns } = cellRendererParams || {};

  const { colDef } = params ?? {};

  const curColumn = columns.find((col) => col.columnId === colDef.field) || {};

  const modifiedParams = {
    ...params,
    colDef: {
      ...params.colDef,
      ...curColumn,
      colId: curColumn.columnId,
      // emptyValueReplacer: EMPTY_VALUE,
    },
  };

  return CellRenderer(modifiedParams);
};

const EngagementSurveyReportsGrid = ({ gridRef }) => {
  const [columnDefs, setColumnDefs] = useState([]);

  const selectedSplitBy = useSelectEngagementSelectedSplitBy();
  const selectedViewBy = useSelectEngagementSelectedViewBy();

  const toast = useToast();

  const isSplitByNone = selectedSplitBy?.value === NONE_ID;

  const timelineDateRange = useSelectEngagementSelectedDateRange();

  //   converting to timestamp
  const timelineRange = useMemo(
    () => ({
      startDate: timelineDateRange?.startDate?.getTime(),
      endDate: timelineDateRange?.endDate?.getTime(),
    }),
    [timelineDateRange]
  );

  const engagementSurveys = useSelectEngagementSurveys();

  const { search } = useLocation();

  const searchParams = new URLSearchParams(search);

  const engagementSurveyId = engagementSurveys?.find(({ uuid }) => uuid === searchParams.get('surveyId'));

  const cohortDetails = useSelectedView();

  const { data: surveyReportsColumnsData } = useGetEngagementSurveyReportsColumns();

  const columns = useMemo(() => surveyReportsColumnsData?.entity ?? [], [surveyReportsColumnsData]);

  const { value: selectedSplitById, label: selectedSplitByDisplayName } = selectedSplitBy;
  const { value: selectedViewById, label: selectedViewByDisplayName } = selectedViewBy;

  // set the column definitions
  // based on the selected view by and split by
  // and the columns from the API
  useEffect(() => {
    if (!columns || columns.length === 0) return;
    const filteredColumns = columns.filter(
      (col) => col.columnId === selectedViewById || col.columnId === selectedSplitById || col.columnId === 'SCORE'
    );
    const newColumnDefs = filteredColumns.map((col) => {
      const colProps = col;
      const additionalProps: {
        filter?: string;
        rowGroup?: boolean;
        pivot?: boolean;
        aggFunc?: string;
        hide?: boolean;
        flex?: number;
      } = {};
      if (colProps.columnId === selectedViewById) {
        additionalProps.flex = 1;
        additionalProps.hide = true;
        additionalProps.rowGroup = true;
      }
      if (isSplitByNone === false) {
        if (colProps.columnId === selectedSplitById) {
          additionalProps.pivot = true;
        }
      }

      return {
        colId: colProps.columnId,
        field: colProps.columnId,
        id: colProps.columnId,
        displayName: colProps.displayName,
        headerName: colProps.displayName,
        description: colProps.description,
        minWidth: 220,
        headerComponent: CustomHeaderCell,
        headerComponentParams: {
          enableMenu: false,
          enableSorting: false,
        },
        emptyValueReplacer: EMPTY_VALUE_ENGAGEMENT_SURVEY_GRID,
        tooltipComponent: SurveyGridModal,

        tooltipValueGetter: (valueGetterParam) => {
          const { value } = valueGetterParam;
          return value || EMPTY_VALUE_ENGAGEMENT_SURVEY_GRID;
        },

        ...getColumnDefinitionByType(colProps),
        sortable: false,
        ...additionalProps,
      };
    });
    setColumnDefs(newColumnDefs);
  }, [columns, isSplitByNone, selectedSplitById, selectedViewById]);

  // register the server side datasource with the grid
  useServerSideDatasource({
    gridRef,
    fetchRows: useCallback(
      ({ filterModel }) =>
        fetchSurveyReportsRows({
          cohortDetails,
          engagementSurveyId,
          engagementSurveys,
          selectedViewById,
          selectedViewByDisplayName,
          selectedSplitById,
          selectedSplitByDisplayName,
          timelineRange,
          toast,
          filterModel,
        }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        cohortDetails,
        engagementSurveys,
        engagementSurveyId,
        selectedSplitByDisplayName,
        selectedSplitById,
        selectedViewByDisplayName,
        selectedViewById,
        timelineRange,
      ]
    ),
    successFallback: useCallback(
      ({ rows: surveyReportData, params }) => {
        const filteredPivotFields =
          surveyReportData?.entity?.pivotFields?.filter(
            (field: string | null | undefined) => field?.split('__')?.[1] === 'SCORE'
          ) || [];
        params.success({
          rowData: surveyReportData?.entity?.rows ?? [],
          rowCount: surveyReportData?.entity?.rows?.length ?? 0,
          pivotResultFields: isSplitByNone ? undefined : [...filteredPivotFields],
        });
      },
      [isSplitByNone]
    ),
  });

  const autoGroupColumnDef = useMemo(
    () => ({
      flex: 1,
      minWidth: 280,
      field: selectedViewById,
      colId: selectedViewById,
      headerName: selectedViewByDisplayName,
      cellRenderer: customGroupCellRenderer,
      sortable: false,
      cellRendererParams: {
        columns,
      },
      headerComponent: CustomHeaderCell,
      headerComponentParams: {
        enableMenu: false,
        enableSorting: false,
      },
    }),
    [columns, selectedViewByDisplayName, selectedViewById]
  );

  const popupParent = useMemo(() => document.querySelector('body'), []);

  return (
    <Box className="engagement-report-grid">
      <AgGridTable
        ref={gridRef}
        columnDefs={columnDefs}
        domLayout="autoHeight"
        pagination
        paginationPageSize={10}
        headerHeight={36}
        rowHeight={56}
        kebabMenuIsRowSpecific={false}
        serverSidePivotResultFieldSeparator="__"
        suppressAggFuncInHeader
        autoGroupColumnDef={autoGroupColumnDef}
        pivotMode={!isSplitByNone}
        rowModelType="serverSide"
        components={{
          customTooltip: SurveyGridModal,
        }}
        // Tooltip configuration
        tooltipShowDelay={200}
        popupParent={popupParent}
        suppressGroupChangesColumnVisibility
      />
    </Box>
  );
};

export { EngagementSurveyReportsGrid };
